import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PayPalScriptProvider,
  PayPalButtons,
  FUNDING,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import {
  IconArrowLeft, IconShieldLock, IconLoader2, IconCheck, IconAlertTriangle,
  IconCreditCard, IconBrandPaypal,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

const CLIENT_ID_ENV = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// Wrapper that only shows spinner while the SDK is booting, then renders the button.
function FundingPanel({ fundingSource, kind, id, onSuccess, onError, onCancel }) {
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  if (isRejected) {
    return (
      <div className="text-errtext text-[13px] flex items-center gap-2">
        <IconAlertTriangle size={14} /> No se pudo cargar PayPal. Revisa tu conexión o desactiva bloqueadores de anuncios.
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex items-center gap-2 text-tert text-[13px] py-4">
        <IconLoader2 size={14} className="animate-spin" /> Preparando pasarela…
      </div>
    );
  }
  return (
    <PayPalButtons
      style={{
        layout: "vertical",
        color: fundingSource === FUNDING.CARD ? "black" : "gold",
        shape: "rect",
        label: fundingSource === FUNDING.CARD ? "pay" : "paypal",
        height: 46,
      }}
      fundingSource={fundingSource}
      forceReRender={[kind, id, fundingSource]}
      createOrder={async () => {
        const { data } = await api.post("/payments/paypal/orders", { kind, id });
        console.info("[PayPal] order created", data.orderID, `$${data.amount} ${data.currency}`);
        return data.orderID;
      }}
      onApprove={async (data) => {
        try {
          const resp = await api.post(`/payments/paypal/orders/${data.orderID}/capture`);
          console.info("[PayPal] capture completed", resp.data.orderID, resp.data.captureID);
          onSuccess(resp.data);
        } catch (e) {
          onError(e);
        }
      }}
      onCancel={onCancel}
      onError={onError}
    />
  );
}

export default function Checkout() {
  const { kind, id } = useParams();
  const nav = useNavigate();
  const { lang } = useI18n();
  const { user } = useAuth();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState("paypal"); // "paypal" | "card"
  const [notice, setNotice] = useState(null); // { kind, text }
  // Resolve the PayPal client_id at RUNTIME from the backend to guarantee
  // frontend↔backend consistency. This makes REACT_APP_PAYPAL_CLIENT_ID a
  // build-time fallback only — a mismatch between env vars can no longer
  // cause INVALID_RESOURCE_ID because both sides always share the same value.
  const [paypalConfig, setPaypalConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/paypal/config");
        setPaypalConfig(data);
      } catch (_) {
        setPaypalConfig({ client_id: CLIENT_ID_ENV, mode: "unknown" });
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (kind === "package") {
          const { data } = await api.get("/payments/packages");
          const pkg = data.find((p) => p.id === id);
          if (!pkg) throw new Error("Paquete no encontrado");
          setSummary({
            kind, id,
            title: pkg.name,
            description: pkg.description,
            amount: pkg.amount,
            currency: (pkg.currency || "usd").toUpperCase(),
            period_label: pkg.period_label,
          });
        } else if (kind === "course") {
          const { data } = await api.get(`/aula/courses/${id}`);
          setSummary({
            kind, id,
            title: data.title,
            description: data.subtitle || data.description?.slice(0, 140),
            amount: Number(data.price || 0),
            currency: "USD",
            period_label: "one_time",
          });
        } else {
          throw new Error("Ruta de checkout inválida");
        }
      } catch (e) {
        setLoadError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [kind, id]);

  const handleSuccess = (result) => {
    // If the purchase is a Family plan, backend flags family_space_pending
    // and returns /app/checkout/family-setup/{orderID}. Route directly there —
    // NO grants are applied until the buyer completes the setup form.
    if (result.family_space_pending && result.redirect?.includes("/family-setup/")) {
      nav(result.redirect);
      return;
    }
    // Otherwise show the standard success screen
    const params = new URLSearchParams({
      orderID: result.orderID,
      captureID: result.captureID || "",
      amount: String(result.amount || summary?.amount || ""),
      currency: (result.currency || summary?.currency || "usd").toUpperCase(),
      redirect: result.redirect || "/app/billing",
    });
    nav(`/app/checkout/success?${params.toString()}`);
  };

  const handleError = (err) => {
    console.error("[PayPal] error", err);
    setNotice({
      kind: "error",
      text: lang === "es"
        ? `Hubo un problema con el pago: ${formatApiError(err)}. Puedes intentarlo de nuevo.`
        : `There was a problem with your payment: ${formatApiError(err)}. Try again.`,
    });
  };

  const handleCancel = () => {
    setNotice({
      kind: "warn",
      text: lang === "es"
        ? "Cancelaste el pago antes de completarlo. Nada fue cargado. Puedes intentar de nuevo cuando quieras."
        : "Payment was cancelled before completing. Nothing was charged. Try again anytime.",
    });
  };

  const backTo = kind === "course" ? `/app/aula/${id}` : "/app/billing";

  const scriptOptions = useMemo(() => {
    // Prefer the runtime client_id from the backend — this is the authoritative
    // value. Only fall back to the build-time env var if the config fetch
    // hasn't completed yet.
    const clientId = paypalConfig?.client_id || CLIENT_ID_ENV;
    return {
      clientId,
      currency: (summary?.currency || "USD").toUpperCase(),
      intent: "capture",
      components: "buttons,funding-eligibility",
      "enable-funding": "card,paypal",
      "disable-funding": "credit,paylater,venmo",
    };
  }, [summary?.currency, paypalConfig?.client_id]);

  if (loading) {
    return (
      <div className="wrap-app py-10 text-tert font-mono text-[11px] uppercase">
        <IconLoader2 size={14} className="animate-spin inline mr-2" /> Cargando checkout…
      </div>
    );
  }

  if (loadError || !summary) {
    return (
      <div className="wrap-app py-10">
        <p className="text-errtext text-[14px]">{loadError || "Checkout no disponible"}</p>
        <button className="btn mt-4" onClick={() => nav(backTo)} data-testid="checkout-back">
          <IconArrowLeft size={13} /> Volver
        </button>
      </div>
    );
  }

  const resolvedClientId = paypalConfig?.client_id || CLIENT_ID_ENV;
  if (!resolvedClientId) {
    return (
      <div className="wrap-app py-10">
        <p className="text-errtext text-[14px]" data-testid="checkout-no-clientid">
          PayPal no está configurado. Configura PAYPAL_CLIENT_ID en el backend.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav(backTo)} data-testid="checkout-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Cancelar y volver" : "Cancel & back"}
        </button>
        <span className="t">{lang === "es" ? "Checkout seguro" : "Secure checkout"}</span>
      </div>

      <div className="wrap-app max-w-[860px] py-8" data-testid="checkout-page">
        <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-6">
          {/* LEFT: Order summary */}
          <section className="card" data-testid="checkout-summary">
            <p className="lbl m-0">{lang === "es" ? "Resumen del pedido" : "Order summary"}</p>
            <h2 className="font-display text-[22px] font-medium mt-1 leading-tight" data-testid="checkout-item-title">
              {summary.title}
            </h2>
            {summary.description && (
              <p className="text-muted text-[13px] mt-2 leading-relaxed">{summary.description}</p>
            )}
            <div className="border-t border-line mt-4 pt-4 flex items-center justify-between">
              <span className="text-tert text-[12.5px]">
                {summary.period_label === "monthly" ? (lang === "es" ? "Suscripción mensual" : "Monthly subscription")
                 : summary.period_label === "yearly" ? (lang === "es" ? "Anual" : "Yearly")
                 : (lang === "es" ? "Pago único" : "One-time")}
              </span>
              <span className="font-display text-[26px] font-medium text-teal-deep" data-testid="checkout-total">
                ${Number(summary.amount).toFixed(2)} <span className="text-tert text-[12px] uppercase">{summary.currency}</span>
              </span>
            </div>
            <div className="mt-4 text-[11.5px] text-tert flex items-start gap-2">
              <IconShieldLock size={13} className="mt-0.5 text-teal-deep shrink-0" />
              <span>
                {lang === "es"
                  ? "Pago procesado por PayPal. Chiessa Institute no almacena datos de tu tarjeta."
                  : "Payment processed by PayPal. Chiessa Institute does not store your card details."}
              </span>
            </div>
            {user?.email && (
              <p className="text-tert text-[11.5px] mt-3 font-mono">{user.email}</p>
            )}
          </section>

          {/* RIGHT: Payment panel */}
          <section className="card" data-testid="checkout-payment">
            <p className="lbl m-0">{lang === "es" ? "Método de pago" : "Payment method"}</p>

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-2 mt-3" role="tablist">
              <button
                onClick={() => setTab("paypal")}
                role="tab"
                aria-selected={tab === "paypal"}
                className={`text-left p-3 rounded-lg border transition-all ${
                  tab === "paypal" ? "border-teal bg-teal-soft" : "border-line bg-white hover:border-teal-deep"
                }`}
                data-testid="checkout-tab-paypal"
              >
                <p className="text-[18px] leading-none"><IconBrandPaypal size={20} className="inline" /></p>
                <p className="text-[13px] font-medium mt-1">PayPal</p>
                <p className="text-[10.5px] text-tert mt-0.5">
                  {lang === "es" ? "Con tu cuenta PayPal" : "With your PayPal account"}
                </p>
              </button>
              <button
                onClick={() => setTab("card")}
                role="tab"
                aria-selected={tab === "card"}
                className={`text-left p-3 rounded-lg border transition-all ${
                  tab === "card" ? "border-teal bg-teal-soft" : "border-line bg-white hover:border-teal-deep"
                }`}
                data-testid="checkout-tab-card"
              >
                <p className="text-[18px] leading-none"><IconCreditCard size={20} className="inline" /></p>
                <p className="text-[13px] font-medium mt-1">
                  {lang === "es" ? "Tarjeta" : "Card"}
                </p>
                <p className="text-[10.5px] text-tert mt-0.5">
                  {lang === "es" ? "Débito o crédito" : "Debit or credit"}
                </p>
              </button>
            </div>

            <div className="mt-4">
              {!paypalConfig ? (
                <div className="flex items-center gap-2 text-tert text-[13px] py-4">
                  <IconLoader2 size={14} className="animate-spin" /> Cargando pasarela…
                </div>
              ) : (
                <PayPalScriptProvider options={scriptOptions}>
                {tab === "paypal" ? (
                  <FundingPanel
                    key="paypal-wallet"
                    fundingSource={FUNDING.PAYPAL}
                    kind={summary.kind}
                    id={summary.id}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    onCancel={handleCancel}
                  />
                ) : (
                  <FundingPanel
                    key="paypal-card"
                    fundingSource={FUNDING.CARD}
                    kind={summary.kind}
                    id={summary.id}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    onCancel={handleCancel}
                  />
                )}
                </PayPalScriptProvider>
              )}
            </div>

            {notice && (
              <div
                className={`mt-3 p-3 rounded-lg border-l-[3px] text-[12.5px] ${
                  notice.kind === "error" ? "border-l-errtext bg-errbg text-errtext"
                  : notice.kind === "warn" ? "border-l-warntext bg-warnbg text-warntext"
                  : "border-l-teal bg-teal-soft"
                }`}
                data-testid="checkout-notice"
              >
                <div className="flex items-start gap-2">
                  {notice.kind === "error" ? <IconAlertTriangle size={14} className="mt-0.5 shrink-0" />
                   : notice.kind === "warn" ? <IconAlertTriangle size={14} className="mt-0.5 shrink-0" />
                   : <IconCheck size={14} className="mt-0.5 shrink-0" />}
                  <span>{notice.text}</span>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
